import { useState, useEffect } from 'react';
import { isClient } from './helpers';

export function useKeyPress(targetKey) {
  // State for keeping track of whether key is pressed
  const [keyPressed, setKeyPressed] = useState(false);

  // If pressed key is our target key then set to true
  function downHandler({ key }) {
    if (key === targetKey) {
      setKeyPressed(true);
    }
  }

  // If released key is our target key then set to false
  const upHandler = ({ key }) => {
    if (key === targetKey) {
      setKeyPressed(false);
    }
  };

  // Add event listeners
  useEffect(() => {
    window.addEventListener('keydown', downHandler);
    window.addEventListener('keyup', upHandler);
    // Remove event listeners on cleanup
    return () => {
      window.removeEventListener('keydown', downHandler);
      window.removeEventListener('keyup', upHandler);
    };
  }, []); // Empty array ensures that effect is only run on mount and unmount

  return keyPressed;
}

export const useDimensions = () => {
  const [viewportWidth, setViewportWidth] = useState(isClient ? window.innerWidth : 0);
  const [viewportHeight, setViewportHeight] = useState(isClient ? window.innerHeight : 0);
  const [documentHeight, setDocumentHeight] = useState(isClient ? document.body.scrollHeight : 0);

  useEffect(() => {
    if (isClient) {
      const handleResize = () => {
        setViewportWidth(window.innerWidth);
        setViewportHeight(window.innerHeight);
        setDocumentHeight(document.body.scrollHeight);
      };
      setViewportWidth(window.innerWidth);
      setViewportHeight(window.innerHeight);
      setDocumentHeight(document.body.scrollHeight);
      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  });
  return { viewportWidth, viewportHeight, documentHeight };
};

export const useMousePosition = () => {
  const [mousePosition, setMousePosition] = useState({ x: null, y: null });

  const updateMousePosition = (event) => {
    setMousePosition({ x: event.clientX, y: event.clientY });
  };

  useEffect(() => {
    window.addEventListener('mousemove', updateMousePosition);

    return () => window.removeEventListener('mousemove', updateMousePosition);
  }, []);

  return mousePosition;
};

export const useScrollPosition = () => {
  const [viewportTopPos, setViewportTopPos] = useState(isClient ? window.scrollY : 0);

  const handleScroll = () => {
    setViewportTopPos(window.scrollY);
  };

  useEffect(() => {
    if (isClient) {
      setViewportTopPos(window.scrollY);
      window.addEventListener('scroll', handleScroll, false);
      return () => {
        window.removeEventListener('scroll', handleScroll, false);
      };
    }
  });

  return viewportTopPos;
};
